/**
 * ClaimsSettingsTab - layout for the claims-family settings pages.
 *
 * Route-driven tabs. The base tab (/settings/claims) hosts field-visibility
 * rules; the rest are gated by feature flag and/or RBAC permission:
 *   - Approvals          (approvals flag + claims.loa_actions permission)
 *   - Routing            (claims.edit.global permission, no flag)
 *   - Customer Claims    (tenant admins only)
 *   - Payment Types      (payment-types.view)
 *   - Vouchers           (vouchers flag + voucher.configure)
 *   - Units of Measure   (units-of-measure.view)
 *   - Labels             (claim-labels.view)
 *   - Escalation Reasons (escalation-reasons.view)
 * Tab chrome only renders when more than the base tab is available.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router'
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { useEntityAttribute } from '@/admin/hooks/use-entity-registration'
import {
  useFieldVisibilityRules,
  isClaimScopedFieldVisibilityRule,
} from '@/admin/hooks/use-field-visibility-rules'

type ClaimsTab = { value: string; label: string; path: string }

export function ClaimsSettingsTab() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const approvalsEnabled = useFeatureFlag(FLAGS.APPROVALS)
  const vouchersEnabled = useFeatureFlag(FLAGS.VOUCHERS)
  const { can, isAdmin } = usePermissions()

  // Field Visibility is only worth a tab when the tenant actually has
  // claim-scoped field-visibility rules to manage. The rules query is gated by
  // the same `registration_rules` flag as the section itself, so we only fetch
  // (and only surface the tab) when the capability is available. Without this,
  // the tab renders blank for every tenant that hasn't configured any rules.
  const fieldVisibilityEnabled = useFeatureFlag(FLAGS.REGISTRATION_RULES)
  const { data: fieldVisibilityRules, isLoading: fieldVisibilityLoading } =
    useFieldVisibilityRules({ enabled: fieldVisibilityEnabled })
  const fieldVisibilitySettled = !fieldVisibilityEnabled || !fieldVisibilityLoading
  // While the rules query is still settling, assume the tab is visible so the
  // active-tab highlight doesn't flicker to another tab and back. Once settled,
  // it stays only if the tenant has at least one claim-scoped rule.
  const showFieldVisibility = useMemo(
    () =>
      fieldVisibilityEnabled &&
      (!fieldVisibilitySettled ||
        (fieldVisibilityRules ?? []).some(isClaimScopedFieldVisibilityRule)),
    [fieldVisibilityEnabled, fieldVisibilitySettled, fieldVisibilityRules],
  )

  // Config-list tabs are gated by BOTH an RBAC permission AND the presence
  // of the backing entity attribute for this tenant. A shared role can
  // grant e.g. `payment-types.view` to a tenant that never registered the
  // `claim_payment` entity - without the entity check the empty tab would
  // bleed into tenants that don't have the feature configured.
  const hasPaymentTypes = useEntityAttribute('claim_payment', 'payment_type')
  const hasUnitsOfMeasure = useEntityAttribute('settlement_line_item', 'uom')
  const hasLabels = useEntityAttribute('claim', 'labels')
  const hasEscalationReasons = useEntityAttribute('claim', 'escalation_reason')

  const tabs = useMemo<ClaimsTab[]>(() => {
    const list: ClaimsTab[] = []
    if (showFieldVisibility)
      list.push({ value: 'rules', label: 'Field Visibility', path: '/settings/claims' })
    if (approvalsEnabled && can('claims.loa_actions'))
      list.push({
        value: 'approvals',
        label: 'Approvals',
        path: '/settings/claims/approvals',
      })
    if (can('claims.edit.global'))
      list.push({
        value: 'routing',
        label: 'Routing',
        path: '/settings/claims/routing',
      })
    if (can('claims.edit.global'))
      list.push({
        value: 'reminders',
        label: 'Reminders',
        path: '/settings/claims/reminders',
      })
    if (isAdmin)
      list.push({
        value: 'customer-claims',
        label: t('claimsSettings.customerClaimsTab', 'Customer Claims'),
        path: '/settings/claims/customer-claims',
      })
    if (can('payment-types.view') && hasPaymentTypes)
      list.push({
        value: 'payment-types',
        label: 'Payment Types',
        path: '/settings/claims/payment-types',
      })
    if (vouchersEnabled && can('voucher.configure'))
      list.push({
        value: 'vouchers',
        label: t('claimsSettings.vouchersTab', 'Vouchers'),
        path: '/settings/claims/vouchers',
      })
    if (can('units-of-measure.view') && hasUnitsOfMeasure)
      list.push({
        value: 'units-of-measure',
        label: 'Units of Measure',
        path: '/settings/claims/units-of-measure',
      })
    if (can('claim-labels.view') && hasLabels)
      list.push({
        value: 'labels',
        label: 'Labels',
        path: '/settings/claims/labels',
      })
    if (can('escalation-reasons.view') && hasEscalationReasons)
      list.push({
        value: 'escalation-reasons',
        label: 'Escalation Reasons',
        path: '/settings/claims/escalation-reasons',
      })
    return list
  }, [
    showFieldVisibility,
    approvalsEnabled,
    vouchersEnabled,
    can,
    isAdmin,
    t,
    hasPaymentTypes,
    hasUnitsOfMeasure,
    hasLabels,
    hasEscalationReasons,
  ])

  const onChildRoute = tabs.some(
    (t) => t.value !== 'rules' && location.pathname.endsWith(t.value),
  )

  // The index route (/settings/claims) renders the Field Visibility section.
  // When that tab is hidden but other tabs exist, redirect the index to the
  // first available tab so the user never lands on a blank page. Wait until the
  // rules query has settled to avoid flicker.
  if (
    fieldVisibilitySettled &&
    !showFieldVisibility &&
    !onChildRoute &&
    tabs.length > 0
  ) {
    return <Navigate to={tabs[0].path} replace />
  }

  const active = onChildRoute
    ? tabs.find((t) => location.pathname.endsWith(t.value))!.value
    : (tabs[0]?.value ?? 'rules')

  if (tabs.length <= 1) {
    // Single-page claims settings - no tab chrome.
    return <Outlet />
  }

  return (
    <div className="flex flex-col gap-6">
      <Tabs
        value={active}
        onValueChange={(value) => {
          const target = tabs.find((t) => t.value === value)
          if (target) navigate(target.path)
        }}
      >
        <TabsList variant="line">
          {tabs.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <Outlet />
    </div>
  )
}
